import React from 'react';
import { KpiMeta } from '../config/kpi-config';
import { formatDelta } from '../utils/formatters';
import { tailwindClasses } from '../styles/tokens';

interface DeltaChipProps {
  value: number;
  prevValue: number | null;
  meta: KpiMeta;
  compact?: boolean;
}

const DeltaChip: React.FC<DeltaChipProps> = ({ value, prevValue, meta, compact = false }) => {
  // Calculate delta
  const delta = prevValue !== null ? value - prevValue : 0;

  // Don't show chip if no previous value
  if (prevValue === null) {
    return (
      <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-slate-400`}>
        Ingen data
      </span>
    );
  }

  // Don't show chip if delta is zero (within tolerance)
  if (Math.abs(delta) < 0.1) {
    return (
      <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-slate-400`}>
        Oförändrat
      </span>
    );
  }

  const formattedDelta = formatDelta(delta, meta);

  // Determine arrow direction and color using standardized tokens
  const isPositive = delta > 0;
  const isGood = meta.higherIsBetter === null ? false : (isPositive === meta.higherIsBetter);

  const arrow = isPositive ? '↗' : '↘';
  const colorClass = isGood ? tailwindClasses.positive : tailwindClasses.negative;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${colorClass.bg} ${colorClass.text} ${compact ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5'}`}
      role="img"
      aria-label={`${isPositive ? 'Ökning' : 'Minskning'}: ${formattedDelta}`}
    >
      <span aria-hidden="true">{arrow}</span>
      <span>{formattedDelta}</span>
    </span>
  );
};

export default DeltaChip;
